import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Heart, Shield, Users, Stethoscope } from 'lucide-react';
import '@/App.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useInView(ref) {
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold: 0.1 });
    o.observe(ref.current);
    return () => o.disconnect();
  }, [ref]);
  return v;
}
function Reveal({ children, className = '', delay = 0, direction = 'up' }) {
  const ref = useRef(null);
  const v = useInView(ref);
  const transform = direction === 'left' ? 'translate-x-[-30px]' : direction === 'right' ? 'translate-x-[30px]' : 'translate-y-6';
  return <div ref={ref} className={`${className} transition-all duration-700 ${v ? 'opacity-100 translate-y-0 translate-x-0' : `opacity-0 ${transform}`}`} style={{ transitionDelay: `${delay}ms` }}>{children}</div>;
}

export default function About() {
  const { t } = useLanguage();
  const [siteContent, setSiteContent] = useState({});

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => setSiteContent(r.data)).catch(() => {});
    axios.post(`${API}/analytics/visit`, { page: '/about' }).catch(() => {});
  }, []);

  const values = [
    { icon: Heart, title: 'We Show Up Like People', desc: 'You will be greeted by name. Appointments rarely run more than ten minutes late. The doctor sits down before she talks.' },
    { icon: Shield, title: 'What Stays Here, Stays Here', desc: 'No partner, no parent, no employer is told anything without your written go-ahead. Charts are stored on a private server, not the cloud.' },
    { icon: Users, title: 'Small Team, Same Faces', desc: 'You will not be passed between five different residents. The same doctor and nurse who see you today are the ones who follow up next week.' },
    { icon: Stethoscope, title: 'One-Visit, When Possible', desc: 'We run our own ultrasound and basic lab — most consults, scans and prescriptions can be wrapped up in a single visit.' },
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="py-24 sm:py-32 bg-secondary/50">
        <div className="container mx-auto px-6 md:px-8 text-center">
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">{t('about.title')}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light">{siteContent.about_title || 'About Us'}</h1>
        </div>
      </section>

      {/* About Content */}
      <section className="py-24 sm:py-32">
        <div className="container mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <Reveal direction="left">
              <div className="rounded-2xl overflow-hidden shadow-lg hover-float">
                <img src={siteContent.about_image} alt="About Dr. Preet" className="w-full h-[450px] object-cover" data-testid="about-image" />
              </div>
            </Reveal>
            <Reveal direction="right" delay={200}>
              <div>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">Why This Clinic Exists</h2>
              <p className="text-base text-muted-foreground mt-6 leading-relaxed whitespace-pre-line">{siteContent.about_content || `Dr. Preet opened this clinic in 2011 after years of working in larger hospital systems where women's health visits felt like a conveyor belt. She wanted a place where the door actually closes, where the doctor sits down, and where the words "I don't know yet, let's figure it out together" are okay to say.\n\nFifteen years later, we are still small on purpose. Two doctors, three nurses, one front-desk person who knows your name by your second visit. We see roughly a thousand women a year — from teenagers in for their first exam to women navigating menopause — and we keep it that way so nobody feels like a number.\n\nWe believe good women's healthcare is not complicated. It is taking the time to listen, ordering the test that actually answers the question, and explaining the results in language you would use with a friend. That is the whole philosophy.`}</p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="text-center p-6 bg-card rounded-2xl border border-border">
                  <p className="text-3xl font-heading font-bold text-primary">15+</p>
                  <p className="text-sm text-muted-foreground mt-1">{t('about.yearsExp')}</p>
                </div>
                <div className="text-center p-6 bg-card rounded-2xl border border-border">
                  <p className="text-3xl font-heading font-bold text-primary">5000+</p>
                  <p className="text-sm text-muted-foreground mt-1">{t('about.patients')}</p>
                </div>
              </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 sm:py-32 bg-secondary/50">
        <div className="container mx-auto px-6 md:px-8">
          <Reveal><h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal text-center mb-16">How We Actually Practice</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 100}>
                <Card className="hover-float rounded-2xl border-border text-center card-shimmer relative" data-testid={`value-card-${i}`}>
                  <CardContent className="p-8">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 hover-rotate">
                      <v.icon className="w-7 h-7 text-primary icon-rotate transition-transform" />
                    </div>
                  <h3 className="font-heading text-lg font-medium">{v.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2">{v.desc}</p>
                </CardContent>
              </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="container mx-auto px-6 md:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-normal">Want to See Us?</h2>
          <p className="text-base text-muted-foreground mt-4 max-w-lg mx-auto">Pick up the phone or send us a message — first appointment slots are usually open within the week.</p>
          <Link to="/contact">
            <Button size="lg" className="mt-8 rounded-full px-10" data-testid="about-cta">{t('hero.cta')} <ArrowRight className="ml-2 w-4 h-4" /></Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
